import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';

/**
 * The canvas player (Figma 2045:101937, the "Symphony Live" prototype's): a playhead over what the reader has
 * NOT SEEN yet (`canvasUnseen`), from its oldest item to its newest, which the activity panel follows and whose
 * pause stops the canvas's motion.
 *
 * The track is not linear in time. It is one segment per gap between two items (plus a short lead-in before
 * the first), each weighted by the LOG of its gap (`playerTrack`): a three-hour quiet stretch is somewhat longer
 * than a five-minute one, never a long dead run. The weights share about `PLAYER_MS_PER_ITEM` of real time per
 * item, and no segment is shorter than the row before it takes to type in (plus a beat), so the next row only
 * ever arrives once the last has typed — two rows never type together — while the playhead runs at one steady
 * speed and never stops. Each segment's length on the scrubber IS its real time.
 *
 * Should typing still fall behind (a throttled background tab), the playhead holds on the next item until the
 * panel catches up (`shownRef`) rather than overlapping rows.
 *
 * It plays ONCE (the user's call, 2026-11-03; it used to linger and go round): once the last row has typed in it
 * stops there, every row on screen, `done`. Play then replays it from the start, and so does the canvas coming
 * back on screen (`restart`). `done` is not the reader's pause: what the pause holds still (the octopus's demo
 * loop, its orbit) keeps moving once the replay is over.
 *
 * An item that arrives AFTER it stopped (the track grows while `done`) still plays: it carries on from the last row it
 * showed to the new end, and stops there again — never back to the start. A reader's pause is not `done`, so a
 * paused replay stays paused.
 */
/** The real time a loop spends per item, on average, and the most a whole loop may take. */
export const PLAYER_MS_PER_ITEM = 3_000;
export const PLAYER_MAX_LOOP_MS = 6 * 60_000;
/** The lead-in before the first item, in weight units (one unit is a gap of no time at all). */
const PLAYER_LEAD = 1;
/** The beat between a row finishing its typing and the next one arriving. */
const PLAYER_TYPED_BEAT_MS = 400;
/** How often the playhead's readers re-render while it moves. */
const PLAYER_TICK_MS = 100;

/**
 * Each item's place on the track, in real ms of playback, for item times in ascending order and each row's
 * typing time. The lead-in and each gap are weighted `1 + ln(1 + minutes)` and share the loop's pace (about
 * `PLAYER_MS_PER_ITEM` an item, at most `PLAYER_MAX_LOOP_MS` in all); a gap is never shorter than the row before
 * it takes to type, plus a beat. The track ends on the last item.
 */
export function playerTrack(times: number[], typingMs: number[]): number[] {
  const weights = times.map((at, i) => (i === 0 ? PLAYER_LEAD : 1 + Math.log1p(Math.max(0, at - times[i - 1]) / 60_000)));
  const total = weights.reduce((sum, w) => sum + w, 0);
  const msPerUnit = total > 0 ? Math.min(times.length * PLAYER_MS_PER_ITEM, PLAYER_MAX_LOOP_MS) / total : 0;
  const pos: number[] = [];
  weights.forEach((w, i) => {
    const paced = w * msPerUnit;
    const room = i === 0 ? 0 : (typingMs[i - 1] ?? 0) + PLAYER_TYPED_BEAT_MS;
    pos.push((i === 0 ? 0 : pos[i - 1]) + Math.max(paced, room));
  });
  return pos;
}

/** The real time at a track position: between two items, in proportion; on the lead-in, the first item's. */
export function playerTimeAt(times: number[], pos: number[], u: number): number | null {
  if (!times.length) return null;
  if (u <= pos[0]) return times[0];
  for (let i = 1; i < pos.length; i++) {
    if (u <= pos[i]) return times[i - 1] + ((u - pos[i - 1]) / Math.max(pos[i] - pos[i - 1], 1e-9)) * (times[i] - times[i - 1]);
  }
  return times[times.length - 1];
}

/** How many items stand at or before a track position. */
function reachedAt(pos: number[], u: number): number {
  let n = 0;
  while (n < pos.length && pos[n] <= u + 1e-9) n += 1;
  return n;
}

export interface CanvasPlayer {
  /** 0–1 along the track. */
  progress: number;
  /** How many items the playhead has reached — the rows the panel shows. */
  reached: number;
  /** How many items the track holds. */
  total: number;
  playing: boolean;
  /** The replay played to its end by itself and stopped there — not the reader's pause. */
  done: boolean;
  /** Bumped on a jump (a seek, a replay from the start), so a follower can tell a jump from playing on. */
  jumps: number;
  toggle: () => void;
  /** Put the playhead at a point (0–1) and pause there — the reader's own seek; play resumes from it. */
  seek: (progress: number) => void;
  /** Back to the start of the loop, playing — the canvas coming back on screen. */
  restart: () => void;
  /** The real time at a point of the track (0–1) — the scrubber's hover label. `null` with no items. */
  timeAt: (progress: number) => number | null;
}

/**
 * The player's state, ticking while it plays. `items` are the items' times, ascending, each with the time its
 * row takes to type in — `null` while the data is still loading, which holds the player back. `active` false
 * holds it still (the canvas is not on screen). `shownRef` is how many of the reached rows the panel has
 * finished typing in: the loop's end waits on it, and so does the next item should typing fall behind.
 */
export function useCanvasPlayer(
  active: boolean,
  items: { at: number; typingMs: number }[] | null,
  shownRef?: RefObject<number>,
): CanvasPlayer | null {
  const ready = items !== null;
  const list = useMemo(() => (items ?? []).map((item) => item.at), [items]);
  const pos = useMemo(() => playerTrack(list, (items ?? []).map((item) => item.typingMs)), [list, items]);
  const end = pos.length ? pos[pos.length - 1] : 0;
  // The playhead, in ms of playback along the track.
  // `doneUpTo`: the time of the newest item it had shown when it stopped at its end (null while not `done`). An item
  // is new by its TIME, not by the list's length: a category cleared after the end shrinks the list, and a count would
  // then miss an arrival that brings it back to the old length.
  const [state, setState] = useState<{ u: number; playing: boolean; jumps: number; done: boolean; doneUpTo: number | null }>({
    u: 0,
    playing: true,
    jumps: 0,
    done: false,
    doneUpTo: null,
  });
  const uRef = useRef(0);
  uRef.current = state.u;
  const trackRef = useRef({ pos, end, list });
  trackRef.current = { pos, end, list };

  // The jump counter as the loop reads it, between renders.
  const seenRef = useRef(state.jumps);
  seenRef.current = state.jumps;

  const playing = ready && state.playing && active && list.length > 0;
  useEffect(() => {
    if (!playing) return undefined;
    let raf = 0;
    let last = performance.now();
    let acc = 0;
    let u = uRef.current;
    let seen = seenRef.current;
    // Played again AT the end (after it stopped there, or a pause there), with everything shown, it goes back to
    // the start at once: the reader has had their look.
    let resumedAtEnd = u >= trackRef.current.end;
    const tick = (now: number) => {
      const dt = Math.min(250, now - last);
      last = now;
      const { pos: track, end: stop } = trackRef.current;
      // A seek while playing: carry on from where the reader put the playhead.
      if (seenRef.current !== seen) {
        seen = seenRef.current;
        u = uRef.current;
      }
      // Recounted every frame, so items arriving mid-loop take their places.
      const reached = reachedAt(track, u);
      const shown = shownRef?.current ?? reached;
      if (reached < track.length) {
        // On to the next item — the gap leaves the last row room to type in, so this never waits in practice.
        // Should typing fall behind, it stops on the next item until the panel has caught up.
        u = Math.min(shown < reached ? track[reached] - 1e-6 : track[reached], u + dt);
        acc += dt;
        if (reachedAt(track, u) !== reached || acc >= PLAYER_TICK_MS) {
          acc = 0;
          const v = u;
          setState((s) => ({ ...s, u: v }));
        }
      } else if (shown < reached) {
        // At the end with the last row still typing in: it stops once that row has.
      } else if (resumedAtEnd) {
        // Played again at the end: back to the start.
        resumedAtEnd = false;
        u = 0;
        seen += 1;
        seenRef.current = seen;
        setState((s) => ({ ...s, u: 0, jumps: s.jumps + 1 }));
      } else {
        // The end, every row typed in: it played once, so it stops here.
        u = Math.min(u, stop);
        const v = u;
        const times = trackRef.current.list;
        const upTo = times.length ? times[times.length - 1] : null;
        setState((s) => ({ ...s, u: v, playing: false, done: true, doneUpTo: upTo }));
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      // Keep where the loop had got to — unless a jump (the reader's seek) is what stopped it, which already
      // put the playhead where it belongs.
      if (seenRef.current !== seen) return;
      const v = u;
      setState((s) => ({ ...s, u: v }));
    };
  }, [playing, shownRef]);

  // An item newer than the last one shown arrived after it played to its end: on from the last row it showed (where
  // that row now stands — the track re-paces every gap) to the new end. Not a jump: the rows on screen stay, the new
  // ones type in. Items are ascending by time, so "newer" is the last one's time.
  useEffect(() => {
    const upTo = state.doneUpTo;
    if (!state.done || state.playing || upTo === null || !list.length || list[list.length - 1] <= upTo) return;
    let shown = 0;
    while (shown < list.length && list[shown] <= upTo) shown += 1;
    const from = shown > 0 ? pos[shown - 1] : 0;
    setState((s) => (s.done ? { ...s, u: from, playing: true, done: false, doneUpTo: null } : s));
  }, [state.done, state.playing, state.doneUpTo, list, pos]);

  const toggle = useCallback(() => setState((s) => ({ ...s, playing: !s.playing, done: false, doneUpTo: null })), []);
  const seek = useCallback((progress: number) => {
    const stop = trackRef.current.end;
    // The reader put the playhead here: it stays, paused, until they press play.
    setState((s) => ({ ...s, u: Math.max(0, Math.min(1, progress)) * stop, playing: false, jumps: s.jumps + 1, done: false, doneUpTo: null }));
  }, []);
  const restart = useCallback(() => setState((s) => ({ u: 0, playing: true, jumps: s.jumps + 1, done: false, doneUpTo: null })), []);
  const timeAt = useCallback((progress: number) => playerTimeAt(list, pos, Math.max(0, Math.min(1, progress)) * end), [list, pos, end]);

  if (!ready) return null;
  const u = Math.min(state.u, end);
  return {
    progress: end > 0 ? u / end : 0,
    reached: reachedAt(pos, u),
    total: list.length,
    // Whether it is actually playing — on screen, with something to play — not only the reader's last choice, so
    // what follows it (the panel's typing, the octopus) holds still too, and an empty player reads as Play.
    playing,
    done: state.done,
    jumps: state.jumps,
    toggle,
    seek,
    restart,
    timeAt,
  };
}
